<script setup lang="ts">
import { computed } from 'vue'

export interface Tab {
  id: string
  label: string
  badge?: number
}

const props = withDefaults(defineProps<{
  tabs: Tab[]
  modelValue: string
}>(), {
})

const emit = defineEmits<{
  'update:modelValue': [value: string]
}>()

const activeIndex = computed(() => 
  props.tabs.findIndex(t => t.id === props.modelValue)
)

function selectTab(tab: Tab) {
  emit('update:modelValue', tab.id)
}

function handleKeydown(event: KeyboardEvent) {
  if (event.key === 'ArrowLeft' && activeIndex.value > 0) {
    event.preventDefault()
    emit('update:modelValue', props.tabs[activeIndex.value - 1].id)
  } else if (event.key === 'ArrowRight' && activeIndex.value < props.tabs.length - 1) {
    event.preventDefault()
    emit('update:modelValue', props.tabs[activeIndex.value + 1].id)
  }
}
</script>

<template>
  <div 
    class="tab-nav"
    role="tablist"
    @keydown="handleKeydown"
  >
    <button
      v-for="tab in tabs"
      :key="tab.id"
      class="tab-nav__item"
      :class="{ 'tab-nav__item--active': modelValue === tab.id }"
      role="tab"
      :aria-selected="modelValue === tab.id"
      :tabindex="modelValue === tab.id ? 0 : -1"
      @click="selectTab(tab)"
    >
      {{ tab.label }}
      <span v-if="tab.badge !== undefined" class="tab-nav__badge">
        {{ tab.badge }}
      </span>
    </button>
  </div>
</template>

<style scoped>
.tab-nav {
  display: flex;
  gap: 0;
  border-bottom: 1px solid var(--color-border-soft);
  margin-bottom: 1rem;
}

.tab-nav__item {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  border: none;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  background: transparent;
  color: var(--color-ink-soft);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: color 0.15s ease, border-color 0.15s ease;
  white-space: nowrap;
}

.tab-nav__item:hover:not(.tab-nav__item--active) {
  color: var(--color-ink);
}

.tab-nav__item--active {
  color: var(--color-ink);
  border-bottom-color: var(--color-ink);
}

.tab-nav__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.125rem;
  height: 1.125rem;
  padding: 0 0.375rem;
  font-size: 0.6875rem;
  font-weight: 600;
  background: rgba(245, 158, 11, 0.15);
  color: #B45309;
  border-radius: 9999px;
  font-variant-numeric: tabular-nums;
}

.tab-nav__item:focus-visible {
  outline: 2px solid var(--color-primary);
  outline-offset: -2px;
  border-radius: 2px;
}

/* Responsive */
@media (max-width: 640px) {
  .tab-nav__item {
    padding: 0.5rem 0.75rem;
    font-size: 0.8125rem;
  }
}
</style>
